import { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import useErnie from 'shared/hooks/use-ernie';
import { useStores } from 'src/hooks/use-stores';
import { removeTypename } from 'shared/helpers/utils';
import { useGetMenuCustomization, useUpdateImageBanners } from '../data-access';
import { dutchiePayImageBanner } from './promotion-popup';
import { useMaxImageBanners } from '../image-banner/use-image-banner-form';

type UsePromotionPopUpReturn = {
  isOpen: boolean;
  loading: boolean;
  handleClose: () => void;
  seenPromotionModal: () => void;
  enableBanner: () => Promise<void>;
};

export function usePromotionPopUp(): UsePromotionPopUpReturn {
  const { id: dispensaryId } = useParams<{ id: string }>();
  const history = useHistory();
  const { UI } = useStores();
  const { data, loading } = useGetMenuCustomization();
  const updateData = useUpdateImageBanners();
  const showErnie = useErnie();
  const { cName } = UI.dispensary;
  const maxImageBanners = useMaxImageBanners();
  const [submitting, setSubmitting] = useState(false);

  const dutchiePayImageBannerWithSignUpLink = {
    ...dutchiePayImageBanner,

    link: `https://dutchie.com/pay/signup?cname=${cName}?variant=embedded-menu`,
  };

  const SEEN_DUTCHIE_PAY_MODAL = 'seen_dutchie_pay_image_banner_modal';

  const isDutchiePayEnabledForDispo = UI.dispensary.paymentTypesAccepted?.dutchiePay;
  const hasDispoSeenDutchiePayModal = localStorage.getItem(SEEN_DUTCHIE_PAY_MODAL) === 'true';
  const [isOpen, setIsOpen] = useState((isDutchiePayEnabledForDispo && !hasDispoSeenDutchiePayModal) ?? false);

  function handleClose(): void {
    setIsOpen(false);
  }

  function seenPromotionModal(): void {
    localStorage.setItem(SEEN_DUTCHIE_PAY_MODAL, 'true');
    setIsOpen(false);
  }

  const enableBanner = async (): Promise<void> => {
    const imageBanners = data?.imageBanners ?? [];
    if (imageBanners.length >= maxImageBanners) {
      showErnie('Dutchie Pay banner could not be added due to max banner limit', 'danger');
      return;
    }

    setSubmitting(true);
    try {
      await updateData({
        variables: {
          dispensaryId,
          input: {
            imageBanners: removeTypename([dutchiePayImageBannerWithSignUpLink, ...imageBanners]),
          },
        },
      });

      showErnie('Your image banner has been applied', 'success');
      localStorage.setItem(SEEN_DUTCHIE_PAY_MODAL, 'true');
      history.push(`/dispensaries/${dispensaryId}/customize/image-banner`);
    } catch (err) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(err);
    } finally {
      setSubmitting(false);
    }

    setIsOpen(false);
  };

  return {
    isOpen,
    loading: loading || submitting,
    handleClose,
    seenPromotionModal,
    enableBanner,
  };
}
